/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/

/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import { trackEvent } from 'utils/tracking/tracking';
import {getElementCSSProperty} from 'utils/styles';

import Component from 'inferno-component';
import StoryTitle from 'components/content/shared/story/storytitle/storytitle';
import StoryUrl from 'components/content/shared/story/storyurl/storyurl';
import CTAs from 'components/shared/ctas/ctas';
import Button from 'components/shared/button/button';// STYLES
// (css-in-js, imported as JS from external file, turned into CSS via library used in utils)
import styles from 'components/content/shared/story/story_styles';
import { createStyles } from 'utils/styles';
import chevronRightSolid from 'icons/chevron-right-solid.svg'
import Icon from 'components/shared/icon/icon';
const sheet = createStyles(styles, {link:true});

/* ========== ~~~~~~~~~~ QUESTION SET ~~~~~~~~~~ ========== */
class Story extends Component{
    constructor(props){
        super(props);
        // state to store FontSize of the question.
        // Styles of this component depend on whether or not the rewards are enabled/permitted for the embed.
        // Sets initial values based off the rewards settings and status.
        this.storyEl = null; // stores the DOM elemnt of question container
        this.state = {
            storySize: 'standard',
            hotSpotHover: false
        }; // initial size of the question
        this.availableQuesSizes = ['standard', 'medium', 'large', 'xlarge', 'xxlarge', 'xxxlarge']; // array of all available font sizes
    }

    componentDidMount() {
        // ::: tracking: question loaded ::: //
        trackEvent({ type: 'load', name: 'ceuContentLoad', target: 'story', props: this.props, additionalProps: {story: this.props.storyData.frameData.story}});
        // called here so that the font can be resize as soon as it loads.
        this.adjustFontSize();
    }

    getQuestionContainerDimensions = () => {
        const el = this.storyEl;
        const height = el.clientHeight - parseInt(getElementCSSProperty(el, "padding-top"));
        return { width: el.clientWidth, height };
    };

    getQuestionElementDimensions = () => {
        const el = this.storyEl.firstChild;
        return { width: el.clientWidth, height: el.clientHeight };
    };

    adjustFontSize() {
        const charAmount = this.props.storyData.frameData.story.text.length;
        let storySize = 'standard';
        if ((charAmount >= 50) && (charAmount < 78)) { storySize = 'medium'; }
        if ((charAmount >= 78) && (charAmount < 104)) { storySize = 'large'; }
        if ((charAmount >= 104) && (charAmount < 128)) { storySize = 'xlarge'; }
        if ((charAmount >= 128) && (charAmount < 152)) { storySize = 'xxlarge'; }
        if (charAmount >= 152) { storySize = 'xxxlarge'; }
        // calling in the state update callback function to check if font size needs readjusting
        this.setState({storySize},
            () => this.readjustFontSize())
    };

    // the current height and width of the question elements are grabbed and checked here and font size is adjusted accordingly.
    // Now we have the exact height of question element and so we can adjust the font size accordingly.
    // in very rare cases, this function will be called more than 2 times. So no loop here.
    readjustFontSize() {
        let currQuesSize = this.state.storySize;
        const currQuesSizeIndex = this.availableQuesSizes.indexOf(currQuesSize);
        if(this.storyEl){
            if ((this.getQuestionContainerDimensions().height - this.getQuestionElementDimensions().height) < 10 && currQuesSize !== 'xxxlarge' ) {
                currQuesSize = this.availableQuesSizes[currQuesSizeIndex + 1] ? this.availableQuesSizes[currQuesSizeIndex + 1] : currQuesSize;
                this.setState({
                    storySize: currQuesSize
                }, () => this.readjustFontSize()); // again calling it just in case to see if fonts need more readjustment. Wont be called unless font needs resizing.
            }
        }
    };

    onHotSpotEnter = () => {
        this.setState({ hotSpotHover: true })
    };

    onHotSpotLeave = () => {
        this.setState({ hotSpotHover: false })
    };

    handleLinkClick = (target) => {
        trackEvent({ type: 'click', name: 'ceuContentEngaged', target: 'story', props: this.props, additionalProps: { clickTarget: target }});
        window.open(this.props.storyData.frameData.story.storyUrl, '_blank');
        this.storyResponse('CLICK');
    };

    handleNextClick = () => {
        trackEvent({ type: 'click', name: 'ceuContentEngaged', target: 'story', props: this.props, additionalProps: { clickTarget: 'nextStory' } });
        this.props.advanceFrame(this.props.contentId)
    };

    storyResponse = (engagementType) =>
        this.props.requestStoryResponseResult(
            this.props.storyData.contentId, this.props.storyData.frame,
            false,
            this.getStoryResponseDetails(engagementType),
            this.props.isPubPortalPreview
        );

    getStoryResponseDetails = (engagementType) => ({
        cookieId: this.props.userInfo.userId,
        embedUUID: this.props.embedUUID,
        contentUUID: this.props.storyData.frameData.story.id,
        contentSignature: this.props.storyData.frameData.story.contentSignature,
        contentType: this.props.storyData.frameData.type,
        pageUrl: window.parent.document.location.href,
        contentProperties: {
            headline: this.props.storyData.frameData.story.text,
            storyUrl: this.props.storyData.frameData.story.storyUrl,
            engagementType
        }
    });

    storyEngaged = () => this.props.frameStatus.state === 'engaged';

    render(){
        return(
            <div className={`${sheet.classes.story} hotspothover-${this.state.hotSpotHover} storySize-${this.state.storySize} large-${this.props.enlargedFont}`}
                 ref={(input) => this.storyEl = input}>
                <StoryTitle title={this.props.storyData.frameData.story.text}
                            handleClick={this.handleLinkClick}/>
                {!!this.props.storyData.frameData.story.ctaText &&
                    <StoryUrl storyUrl={this.props.storyData.frameData.story.storyUrl}
                              text={this.props.storyData.frameData.story.ctaText}
                              handleClick={this.handleLinkClick}/>
                }
                {this.storyEngaged() &&
                    <CTAs type={`block`}>
                        <Button klass={`white`} onClick={this.handleNextClick}>
                            <div className={'skipinner'}>
                                <div>{`Next`}</div>
                                <Icon svg={chevronRightSolid} color={'#fff'} size={10} />
                            </div>
                        </Button>
                    </CTAs>
                }
                <div className={'hotspot'}
                     onMouseEnter={() => this.onHotSpotEnter()}
                     onMouseLeave={() => this.onHotSpotLeave()}
                     onClick={() => this.handleLinkClick('hotSpot')}/>
            </div>
        )}
}



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Story;
